import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { getUserNoticeList } from 'components/form/api/member.api';
import { getNotice } from 'components/form/api/admin.api';
import { NoticeItem } from 'components/form/interfaces/admin.interface';
import { WakeUp } from 'common/interface/props';

/**
 * @alias 공지사항 훅 프로퍼티
 */
interface UseNoticeActiveProps {
  /**
   * @alias 솔루션 타입
   */
  solType: WakeUp;
  /**
   * @alias INFORMATION 타입 포함 여부 (기본값: true)
   */
  includeInformation?: boolean;
  /**
   * @alias 미리보기 공지 ID (preview 모드)
   */
  previewNoticeId?: number;
}

/**
 * @alias 공지사항 훅 반환 타입
 */
interface UseNoticeActiveReturn {
  /**
   * @alias INFORMATION 타입 관련
   */
  informationNotices: NoticeItem[];
  currentInformationIndex: number;
  setCurrentInformationIndex: (index: number) => void;
  handleInformationClose: () => void;
  handleInformationDontShowAgain: (checked: boolean) => void;

  /**
   * @alias URGENT 타입 관련
   */
  urgentNotices: NoticeItem[];
  showUrgentNotice: boolean;
  setShowUrgentNotice: (show: boolean) => void;
  currentUrgentIndex: number;
  setCurrentUrgentIndex: (index: number) => void;
  handleUrgentClose: () => void;
}

export const useNoticeActive = ({ solType, includeInformation = true, previewNoticeId }: UseNoticeActiveProps): UseNoticeActiveReturn => {
  // INFORMATION 타입 상태
  const [informationNotices, setInformationNotices] = useState<NoticeItem[]>([]);
  const [currentInformationIndex, setCurrentInformationIndex] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  // URGENT 타입 상태
  const [urgentNotices, setUrgentNotices] = useState<NoticeItem[]>([]);
  const [showUrgentNotice, setShowUrgentNotice] = useState<boolean>(false);
  const [currentUrgentIndex, setCurrentUrgentIndex] = useState<number>(0);

  /**
   * @alias 미리보기 공지 데이터 조회 (preview 모드)
   */
  const { data: previewNoticeData } = useQuery({
    queryKey: ['previewNotice', previewNoticeId],
    queryFn: async () => {
      const result = await getNotice(previewNoticeId!);
      return result;
    },
    enabled: !!previewNoticeId,
  });

  /**
   * @alias 공지사항 데이터 조회
   */
  const { data: noticeListData } = useQuery({
    queryKey: ['noticeList', solType],
    queryFn: async () => {
      const result = await getUserNoticeList({
        solType,
      });
      return result;
    },
    enabled: !!solType && solType !== 'ALL' && !previewNoticeId, // preview 모드가 아닐 때만 조회
    staleTime: 1000 * 60 * 1, // 1분
    refetchInterval: 1000 * 60 * 1, // 1분마다 자동으로 재조회
  });

  /**
   * @alias 미리보기 공지 데이터 처리 (preview 모드)
   */
  useEffect(() => {
    if (previewNoticeData?.success && previewNoticeData.notice) {
      const notice = previewNoticeData.notice;

      if (notice.noticeType === 'INFORMATION') {
        setInformationNotices([notice]);
        setCurrentInformationIndex(0);
      } else if (notice.noticeType === 'URGENT') {
        setUrgentNotices([notice]);
        setShowUrgentNotice(true);
        setCurrentUrgentIndex(0);
      }
    }
  }, [previewNoticeData]);

  /**
   * @alias 공지사항 데이터 설정 및 필터링
   */
  useEffect(() => {
    // preview 모드일 때는 일반 공지 로직 실행 안 함
    if (previewNoticeId) return;

    if (noticeListData?.success && noticeListData.notices) {
      // URGENT 타입은 "다시 보지 않기" 필터링에서 제외하고 항상 포함 (로그인 차단을 위해 필수)
      const urgentNoticesFiltered = noticeListData.notices.filter((notice) => notice.noticeType === 'URGENT');
      setUrgentNotices(urgentNoticesFiltered);

      // INFORMATION 타입은 includeInformation이 true일 때만 처리
      if (includeInformation) {
        // 다시 보지 않기를 선택하지 않은 INFORMATION 타입 공지만 필터링
        const informationNoticesFiltered = noticeListData.notices.filter((notice) => {
          if (notice.noticeType !== 'INFORMATION') return false;
          const hideKey = `hideNotice_${notice.noticeId}`;
          return !localStorage.getItem(hideKey);
        });

        // INFORMATION 타입은 최초 로딩 시에만 페이지 진입 시 팝업으로 표시
        if (isInitialLoad && informationNoticesFiltered.length > 0) {
          setInformationNotices(informationNoticesFiltered);
          setCurrentInformationIndex(0);
          setIsInitialLoad(false);
        }
      }
    }
  }, [noticeListData, isInitialLoad, includeInformation, previewNoticeId]);

  /**
   * @alias INFORMATION 타입 공지 닫기 핸들러
   */
  const handleInformationClose = () => {
    if (currentInformationIndex + 1 < informationNotices.length) {
      setCurrentInformationIndex(currentInformationIndex + 1);
    } else {
      setCurrentInformationIndex(informationNotices.length); // 모달 닫기
    }
  };

  /**
   * @alias INFORMATION 타입 "다시 보지 않기" 핸들러
   */
  const handleInformationDontShowAgain = (checked: boolean) => {
    if (checked) {
      const hideKey = `hideNotice_${informationNotices[currentInformationIndex].noticeId}`;
      localStorage.setItem(hideKey, '1');

      // 체크 후 현재 공지를 목록에서 제거하고 다음 공지로 이동
      const updatedNotices = informationNotices.filter((_, idx) => idx !== currentInformationIndex);
      setInformationNotices(updatedNotices);

      // 인덱스 조정 (현재 위치에서 다음 공지 표시, 없으면 모달 닫기)
      if (updatedNotices.length === 0 || currentInformationIndex >= updatedNotices.length) {
        setCurrentInformationIndex(updatedNotices.length);
      }
    }
  };

  /**
   * @alias URGENT 타입 공지 닫기 핸들러
   */
  const handleUrgentClose = () => {
    if (currentUrgentIndex + 1 < urgentNotices.length) {
      setCurrentUrgentIndex(currentUrgentIndex + 1);
    } else {
      setShowUrgentNotice(false);
      setCurrentUrgentIndex(0);
    }
  };


  return {
    // INFORMATION 타입
    informationNotices,
    currentInformationIndex,
    setCurrentInformationIndex,
    handleInformationClose,
    handleInformationDontShowAgain,

    // URGENT 타입
    urgentNotices,
    showUrgentNotice,
    setShowUrgentNotice,
    currentUrgentIndex,
    setCurrentUrgentIndex,
    handleUrgentClose,
  };
};
